import FormSection from "../../../common/components/functional/formSection/FormSection";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectTalentById,
  updateTalentSkills,
  updateTalentWorkTitles,
} from "../../talents/talentsSlice";
import { Talent, talentIsDeactivated } from "../../talents/talentAPI";
import { useParams } from "react-router-dom";
import { ContentKey } from "../TalentDetails";
import isEmpty from "../../../common/helpers/isEmpty";
import WorkTitleRows from "./WorkTitleRows";
import { Skill, WorkTitle } from "../../orders/orderAPI";
import useMessage from "../../../hooks/useMessage";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import { Grid } from "@mui/material";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import { canExpertEditTalentProfile } from "../../user/accessRightChecks";

type Props = {
  handleOpenEditModal: (content: ContentKey) => void;
};
const TalentWorkTitles = ({ handleOpenEditModal }: Props) => {
  const { t } = useTranslation(["talentProfile", "common"]);
  const { talentId } = useParams();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();

  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const removeWorkTitleCallback = (workTitleId: string) => {
    const currentWorkTitles = talent.workTitles?.values || [];
    const currentWorkTitlesVersion = talent.workTitles?.version || 0;
    const currentSkills = talent.skills?.values || [];
    const currentSkillsVersion = talent.skills?.version || 0;

    if (talentId && !isEmpty(currentWorkTitles) && currentWorkTitlesVersion) {
      const newWorkTitles = currentWorkTitles.filter(
        (workTitle: WorkTitle) => workTitle.id !== workTitleId,
      );

      // Get unique fieldOfWorkIds from new work titles
      const newFieldOfWorkIds = Array.from(
        new Set(newWorkTitles.map((wt) => wt.fieldOfWorkId)),
      );

      // Filter out skills that are not related to the new work titles
      const newSkills = currentSkills.filter((skill: Skill) =>
        newFieldOfWorkIds.includes(skill.fieldOfWorkId),
      );

      const skillsChanged = newSkills.length < currentSkills.length;

      dispatch(
        updateTalentWorkTitles({
          talentId: talentId,
          workTitles: newWorkTitles,
          version: currentWorkTitlesVersion,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          // If no skills have changed, we can skip updating them
          if (!skillsChanged) {
            sendMessage(t("talentProfile:workTitlesUpdated"), "success");
            return;
          }

          // Update skills
          dispatch(
            updateTalentSkills({
              talentId: talentId,
              skills: newSkills,
              version: currentSkillsVersion,
            }),
          )
            .unwrap()
            .then(() =>
              sendMessage(
                t("talentProfile:workTitlesAndSkillsUpdated"),
                "success",
              ),
            );
        }
      });
    }
  };

  const mappedWorkTitles =
    talent?.workTitles?.values.map((workTitle) => ({
      ...workTitle,
      // Use the existing name if present; otherwise, fallback to id
      name: workTitle.name ?? workTitle.id,
    })) || [];

  return (
    <FormSection title={t("common:workTitles")} margin>
      {!!talent.workTitles && !!talent.workTitles.values && (
        <WorkTitleRows
          workTitles={mappedWorkTitles}
          removeWorkTitleCallback={removeWorkTitleCallback}
        />
      )}
      <Grid container spacing={2} sx={{ marginTop: "5px" }}>
        <Grid item xs={3} />
        <Grid item xs={9}>
          <AddButton
            label={t("talentProfile:addWorkTitle")}
            onClick={() => handleOpenEditModal("workTitles")}
            accessRightChecks={[canExpertEditTalentProfile]}
            disabled={talentIsDeactivated(talent)}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default TalentWorkTitles;
